/**
 * DLQ Viewer Page - Dead Letter Queue management
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { dlqApi, DLQItem, DLQItemDetail } from '../api/dlq';
import { useWebSocketEvent } from '../hooks/useWebSocket';

interface DLQListResponse {
  data: DLQItem[];
  total: number;
  page: number;
  per_page: number;
  pages: number;
}

function formatAge(dateString: string) {
  const date = new Date(dateString);
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const minutes = Math.floor(diff / (1000 * 60));
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  if (minutes < 1) return 'щойно';
  if (minutes < 60) return `${minutes} хв`;
  if (hours < 24) return `${hours} год`;
  return `${days} д`;
}

export default function DLQViewer() {
  const navigate = useNavigate();
  const [items, setItems] = useState<DLQItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedItem, setSelectedItem] = useState<DLQItemDetail | null>(null);
  const [showDetail, setShowDetail] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 50,
    total: 0,
    pages: 0,
  });

  const loadItems = async () => {
    try {
      setLoading(true);
      setError(null);
      const response: DLQListResponse = await dlqApi.getList({
        page: pagination.page,
        per_page: pagination.per_page,
      });
      setItems(response.data);
      setPagination({
        page: response.page,
        per_page: response.per_page,
        total: response.total,
        pages: response.pages,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Помилка завантаження');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadItems();
  }, [pagination.page]);

  useWebSocketEvent('dlq_update', (event) => {
    if (event.action === 'retried' || event.action === 'resolved') {
      setItems((prev) => prev.filter((item) => item.id !== event.dlq_id));
      setPagination((prev) => ({ ...prev, total: prev.total - 1 }));
    }
  });

  const handleViewDetails = async (itemId: string) => {
    try {
      const detail = await dlqApi.getDetail(itemId);
      setSelectedItem(detail);
      setShowDetail(true);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Помилка завантаження деталей');
    }
  };

  const handleRetry = async (itemId: string) => {
    if (!confirm('Повторити виконання задачі?')) return;
    try {
      const result = await dlqApi.retry(itemId);
      if (result.success) {
        alert(`Задача поставлена в чергу: ${result.task_name}`);
        loadItems();
      } else {
        alert(`Помилка: ${result.error}`);
      }
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Помилка повтору');
    }
  };

  const handleDismiss = async (itemId: string) => {
    if (!confirm('Відмітити як вирішене без повтору?')) return;
    try {
      const result = await dlqApi.dismiss(itemId);
      if (result.success) {
        alert('Задачу відмічено як вирішену');
        loadItems();
      } else {
        alert(`Помилка: ${result.error}`);
      }
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Помилка dismiss');
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 24, fontWeight: 600, margin: 0 }}>
          ⚠️ Dead Letter Queue
        </h1>
        <p style={{ color: '#8c8c8c', margin: '4px 0 0' }}>
          Невдалі задачі, що потребують уваги ({pagination.total})
        </p>
      </div>

      {/* Loading state */}
      {loading && (
        <div style={loadingBoxStyle}>Завантаження...</div>
      )}

      {/* Error state */}
      {error && (
        <div style={errorBoxStyle}>
          <div style={{ color: '#cf1322' }}>{error}</div>
          <button onClick={loadItems} style={errorLinkStyle}>
            Спробувати ще раз
          </button>
        </div>
      )}

      {/* DLQ items */}
      {!loading && !error && (
        <>
          {items.length === 0 ? (
            <div style={emptyBoxStyle}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>✅</div>
              <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 6 }}>
                Все працює!
              </div>
              <div style={{ color: '#8c8c8c' }}>Невдалих задач немає</div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {items.map((item) => (
                <div key={item.id} style={dlqCardStyle}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, flexWrap: 'wrap' }}>
                    <h3 style={{ fontSize: 16, fontWeight: 600, margin: 0, color: '#1a1a1a' }}>
                      {item.task_name}
                    </h3>
                    <Tag color="#ff4d4f">{formatAge(item.failed_at)} тому</Tag>
                    {item.retry_count > 0 && (
                      <Tag color="#faad14">Спроб: {item.retry_count}</Tag>
                    )}
                  </div>

                  <div style={{ fontSize: 13, color: '#595959', marginBottom: 8 }}>
                    <strong>Замовлення:</strong>{' '}
                    <button
                      onClick={() => navigate(`/orders/${item.order_id}`)}
                      style={linkBtnStyle}
                    >
                      {item.order_id}
                    </button>
                  </div>

                  <div style={errorMessageStyle}>
                    <strong>Помилка:</strong> {item.error_message}
                  </div>

                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <button
                      onClick={() => handleRetry(item.id)}
                      style={primaryBtnStyle}
                    >
                      🔄 Повторити
                    </button>
                    <button
                      onClick={() => handleDismiss(item.id)}
                      style={secondaryBtnStyle}
                    >
                      ✓ Вирішено
                    </button>
                    <button
                      onClick={() => handleViewDetails(item.id)}
                      style={ghostBtnStyle}
                    >
                      🔍 Детальніше
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {pagination.pages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', gap: 4, marginTop: 16 }}>
              {Array.from({ length: pagination.pages }, (_, i) => i + 1).map(
                (page) => (
                  <button
                    key={page}
                    onClick={() => setPagination((prev) => ({ ...prev, page }))}
                    style={{
                      padding: '6px 12px',
                      border: `1px solid ${page === pagination.page ? '#1677ff' : '#d9d9d9'}`,
                      background: page === pagination.page ? '#e6f4ff' : '#fff',
                      color: page === pagination.page ? '#1677ff' : '#595959',
                      borderRadius: 6,
                      fontSize: 13,
                      cursor: 'pointer',
                      minWidth: 36,
                    }}
                  >
                    {page}
                  </button>
                )
              )}
            </div>
          )}
        </>
      )}

      {/* Detail Modal */}
      {showDetail && selectedItem && (
        <div style={overlayStyle} onClick={() => setShowDetail(false)}>
          <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h2 style={{ fontSize: 20, fontWeight: 600, margin: 0 }}>
                Деталі задачі
              </h2>
              <button
                onClick={() => setShowDetail(false)}
                style={closeBtnStyle}
              >
                ×
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <DetailRow title="Назва задачі">
                <span style={{ color: '#1a1a1a' }}>{selectedItem.task_name}</span>
              </DetailRow>

              <DetailRow title="Аргументи">
                <pre style={preStyle}>
                  {JSON.stringify(selectedItem.task_args, null, 2)}
                </pre>
              </DetailRow>

              <DetailRow title="Помилка">
                <div style={errorMessageStyle}>{selectedItem.error_message}</div>
              </DetailRow>

              {selectedItem.stack_trace && (
                <DetailRow title="Stack Trace">
                  <pre style={{ ...preStyle, fontSize: 11 }}>
                    {selectedItem.stack_trace}
                  </pre>
                </DetailRow>
              )}

              {selectedItem.last_state && (
                <DetailRow title="Останній стан">
                  <pre style={preStyle}>
                    {JSON.stringify(selectedItem.last_state, null, 2)}
                  </pre>
                </DetailRow>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <DetailRow title="Час помилки">
                  <span>{new Date(selectedItem.failed_at).toLocaleString('uk-UA')}</span>
                </DetailRow>
                <DetailRow title="Кількість спроб">
                  <span>{selectedItem.retry_count}</span>
                </DetailRow>
              </div>
            </div>

            <div style={{ marginTop: 24, display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
              <button onClick={() => setShowDetail(false)} style={secondaryBtnStyle}>
                Закрити
              </button>
              <button
                onClick={() => {
                  handleRetry(selectedItem.id);
                  setShowDetail(false);
                }}
                style={primaryBtnStyle}
              >
                🔄 Повторити
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Sub-components ──────────────────────────────────────────────────────

function Tag({ children, color }: { children: React.ReactNode; color: string }) {
  return (
    <span style={{
      display: 'inline-block',
      padding: '2px 8px',
      borderRadius: 12,
      fontSize: 12,
      fontWeight: 500,
      background: `${color}15`,
      color: color,
      border: `1px solid ${color}40`,
    }}>
      {children}
    </span>
  );
}

function DetailRow({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <div style={{ fontSize: 13, fontWeight: 600, color: '#595959', marginBottom: 4 }}>
        {title}
      </div>
      {children}
    </div>
  );
}

// ── Styles ──────────────────────────────────────────────────────────────

const loadingBoxStyle: React.CSSProperties = {
  background: '#fff',
  borderRadius: 8,
  border: '1px solid #f0f0f0',
  padding: 40,
  textAlign: 'center',
  color: '#8c8c8c',
};

const errorBoxStyle: React.CSSProperties = {
  background: '#fff2f0',
  border: '1px solid #ffccc7',
  borderRadius: 8,
  padding: 16,
  marginBottom: 16,
};

const errorLinkStyle: React.CSSProperties = {
  marginTop: 8,
  background: 'transparent',
  border: 'none',
  color: '#cf1322',
  cursor: 'pointer',
  textDecoration: 'underline',
  padding: 0,
  fontSize: 13,
};

const emptyBoxStyle: React.CSSProperties = {
  background: '#fff',
  borderRadius: 8,
  border: '1px solid #f0f0f0',
  padding: 40,
  textAlign: 'center',
};

const dlqCardStyle: React.CSSProperties = {
  background: '#fff',
  borderRadius: 8,
  border: '1px solid #f0f0f0',
  borderLeft: '4px solid #ff4d4f',
  padding: 20,
};

const errorMessageStyle: React.CSSProperties = {
  background: '#fff2f0',
  border: '1px solid #ffccc7',
  color: '#a8071a',
  padding: '8px 12px',
  borderRadius: 6,
  fontSize: 13,
  marginBottom: 12,
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
};

const linkBtnStyle: React.CSSProperties = {
  background: 'transparent',
  border: 'none',
  color: '#1677ff',
  cursor: 'pointer',
  textDecoration: 'underline',
  fontSize: 13,
  padding: 0,
  fontFamily: 'monospace',
};

const primaryBtnStyle: React.CSSProperties = {
  padding: '6px 14px',
  background: '#1677ff',
  color: '#fff',
  border: 'none',
  borderRadius: 6,
  fontSize: 13,
  fontWeight: 500,
  cursor: 'pointer',
};

const secondaryBtnStyle: React.CSSProperties = {
  padding: '6px 14px',
  background: '#fff',
  color: '#595959',
  border: '1px solid #d9d9d9',
  borderRadius: 6,
  fontSize: 13,
  fontWeight: 500,
  cursor: 'pointer',
};

const ghostBtnStyle: React.CSSProperties = {
  padding: '6px 14px',
  background: 'transparent',
  color: '#1677ff',
  border: '1px solid #91caff',
  borderRadius: 6,
  fontSize: 13,
  fontWeight: 500,
  cursor: 'pointer',
};

const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0, 0, 0, 0.45)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 16,
  zIndex: 1000,
};

const modalStyle: React.CSSProperties = {
  background: '#fff',
  borderRadius: 12,
  boxShadow: '0 6px 24px rgba(0, 0, 0, 0.16)',
  maxWidth: 800,
  width: '100%',
  maxHeight: '90vh',
  overflowY: 'auto',
  padding: 24,
};

const closeBtnStyle: React.CSSProperties = {
  background: 'transparent',
  border: 'none',
  fontSize: 28,
  cursor: 'pointer',
  color: '#8c8c8c',
  lineHeight: 1,
  padding: 0,
};

const preStyle: React.CSSProperties = {
  background: '#fafafa',
  border: '1px solid #f0f0f0',
  borderRadius: 6,
  padding: 12,
  fontSize: 12,
  overflowX: 'auto',
  margin: 0,
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
};
